// Vocabulary for the SM-507 Asset Rates page.
//
// The page's one hard requirement is that EVERY asset–currency
// combination appears, including the ones nobody has priced — a table of
// only the rates that exist can never show the gap that stops a deposit
// from crediting. So the rows are built from the cross product of the
// asset catalogue and the creditable currencies, and a rate is something
// a row may or may not have rather than something that makes a row.
import type { CryptoAsset, CryptoRate } from "@/lib/wallet-types";
import { parseTokenToBase } from "@/lib/tokenFmt";
import { RATE_UNIT_MAJOR } from "@/lib/wallet-queries";

/**
 * The currencies a deposit may credit, in display order. GREEN is
 * absent on purpose and not as an oversight: GREEN is earned, granted
 * or converted into, never bought, and the service rejects a rate that
 * names it. Offering the combination would offer a form that cannot be
 * saved.
 */
export const RATE_CURRENCIES = ["GOLD", "RED"] as const;

/**
 * Effective minor units credited per whole token unit. Rates set since
 * SM-244 are always 1:1 × spark_minor_per_unit; older rows may carry a
 * legacy numerator/denominator pair, so the arithmetic stays here for
 * history display.
 *
 * BigInt with a FLOOR, not `a / b * c` (SM-513 AC6). The float version
 * handed <CurrencyAmount> a fraction for any rate_den that does not
 * divide out — 1/3 × 10000 is 3333.333… — and formatSpark's integer
 * path truncates with Math.trunc, so the page silently disagreed with
 * the service by up to a minor unit. Flooring here is not a rounding
 * preference either: it is the direction crypto-wallet-service floors,
 * and a preview that rounds the other way promises money the credit
 * will not pay.
 */
export const effectiveMinorPerUnit = (r: CryptoRate): number => {
  const num = BigInt(Math.trunc(r.rate_num)) * BigInt(Math.trunc(r.spark_minor_per_unit));
  const den = BigInt(Math.trunc(r.rate_den) || 1);
  if (den <= 0n) return 0;
  return Number(num / den);
};

/**
 * How many decimal places an operator may type for a currency whose
 * wire scale is `unitMinor` — 4 for the platform's 10 000. Derived by
 * counting the zeros rather than assumed, so a currency that ever
 * changes scale changes this with it.
 */
export function rateDecimals(unitMinor: number): number {
  let n = Math.trunc(unitMinor);
  let dp = 0;
  while (n > 1 && n % 10 === 0) {
    n /= 10;
    dp += 1;
  }
  return n === 1 ? dp : 0;
}

/**
 * An operator-typed rate — WHOLE currency units per one whole token —
 * to the minor units the wire carries. Null when the text is not a
 * usable amount or asks for more precision than the currency has.
 *
 * This function is SM-513. The editor used to scale the entry by 100,
 * a number that belongs to nothing: the platform's currencies are 10
 * 000 minor units to the unit, so an operator who set "1 USDT = 100
 * RED" stored a rate of 1.00 RED per token and every deposit credited
 * a hundredth of what they meant. The scale may only ever come from
 * the currency — hence the required argument, and hence no default.
 */
export function rateMajorToMinor(text: string, unitMinor: number): number | null {
  const base = parseTokenToBase(text, rateDecimals(unitMinor));
  if (base === null) return null;
  const n = Number(base);
  return Number.isSafeInteger(n) ? n : null;
}

/**
 * The inverse, for prefilling the editor from the live rate: minor
 * units per token → the figure an operator would type. Trailing zeros
 * are dropped so a rate of 100 reads "100" rather than "100.0000".
 */
export function rateMinorToMajor(minor: number, unitMinor: number): string {
  const dp = rateDecimals(unitMinor);
  const negative = minor < 0;
  const digits = String(Math.abs(Math.trunc(minor))).padStart(dp + 1, "0");
  const int = digits.slice(0, digits.length - dp) || "0";
  const frac = dp > 0 ? digits.slice(digits.length - dp).replace(/0+$/, "") : "";
  return `${negative ? "-" : ""}${int}${frac ? `.${frac}` : ""}`;
}

/**
 * The credit a deposit of `amountBase` would earn at a rate of
 * `minorPerUnit` minor units per whole token — the same integer
 * arithmetic crypto-wallet-service runs, in BigInt, flooring the same
 * way:
 *
 *   credited = floor(amount_base × rate_num × minor_per_unit
 *                    / (10^token_decimals × rate_den))
 *
 * Reimplemented here rather than fetched because the operator needs the
 * answer for a rate they have NOT saved yet (AC4), and the service can
 * only price the rate it holds. `remainder` is the numerator the floor
 * discarded, so the panel can say when a deposit is worth less than one
 * minor unit instead of silently previewing zero.
 */
export function previewCreditMinor(
  amountBase: string,
  tokenDecimals: number,
  minorPerUnit: number,
  rateNum = 1,
  rateDen = 1,
): { credited: bigint; remainder: bigint } | null {
  if (!/^\d+$/.test(amountBase.trim())) return null;
  if (!Number.isInteger(tokenDecimals) || tokenDecimals < 0) return null;
  if (!(minorPerUnit > 0) || !(rateNum > 0) || !(rateDen > 0)) return null;
  const num = BigInt(amountBase.trim()) * BigInt(rateNum) * BigInt(minorPerUnit);
  const den = 10n ** BigInt(tokenDecimals) * BigInt(rateDen);
  return { credited: num / den, remainder: num % den };
}

/**
 * Whether a rate's UNIT provenance is unknown — a row written before
 * SM-513, whose stored figure may be a hundredth of what the operator
 * intended. `unit_version` is absent on every such row, and absent must
 * read as legacy: treating silence as agreement would empty the review
 * list of exactly the rows it exists to hold.
 *
 * A flagged rate still credits. What it credits is what it has always
 * credited and what every completed deposit froze; only an operator can
 * say whether that was the intent, so the console asks rather than
 * multiplying by 100 on their behalf.
 */
export const needsUnitReview = (r: CryptoRate | null | undefined): boolean =>
  !!r && (r.unit_version ?? 1) < RATE_UNIT_MAJOR;

/**
 * A stored rate's credited currency. Rows written before SM-416 carry
 * no currency at all, and absent means GOLD — those rates were set when
 * there was one currency, and reading them as anything else would
 * relabel historical credits.
 */
export const rateCurrency = (r: CryptoRate) => r.credited_currency || "GOLD";

/** One (asset, currency) mapping with its rate, if it has one. */
export interface RateRow {
  asset: CryptoAsset;
  networkName: string;
  currency: string;
  /** The live rate, or null when this mapping has never been priced. */
  active: CryptoRate | null;
  /** Superseded and retired rows, newest first. */
  history: CryptoRate[];
  /** Whether the asset's rates have arrived yet. */
  loading: boolean;
  error?: unknown;
}

export interface RateRowsInput {
  assets: CryptoAsset[];
  networkNames: Record<string, string>;
  /** Per-asset rate list, exactly as the service returns it. */
  ratesByAsset: Record<
    string,
    { rates?: CryptoRate[]; loading?: boolean; error?: unknown } | undefined
  >;
}

/**
 * The complete matrix, asset-major then currency in display order.
 *
 * The service keeps exactly one enabled row per (asset, currency) and
 * returns the list newest-first, so the first enabled row of a currency
 * is that mapping's live rate. Rows predating the single-active
 * migration may still say enabled — everything after the first is
 * history and must never render as active.
 */
export function buildRateRows({
  assets,
  networkNames,
  ratesByAsset,
}: RateRowsInput): RateRow[] {
  const rows: RateRow[] = [];
  for (const asset of assets) {
    const entry = ratesByAsset[asset.asset_id];
    const all = entry?.rates ?? [];
    for (const currency of RATE_CURRENCIES) {
      const mine = all.filter((r) => rateCurrency(r) === currency);
      const active = mine.find((r) => r.enabled) ?? null;
      rows.push({
        asset,
        networkName: networkNames[asset.network_id] || asset.network_id,
        currency,
        active,
        history: mine.filter((r) => r !== active),
        loading: entry?.loading ?? false,
        error: entry?.error,
      });
    }
  }
  return rows;
}

export interface RateFilters {
  /** Free text over symbol, asset id, network and currency. */
  q?: string;
  currency?: string;
  network?: string;
  /** Only mappings with no live rate — the configuration gaps. */
  missing?: boolean;
  /**
   * Only live rates whose unit provenance is unknown (SM-513) — the
   * operator review list. Separate from `missing` on purpose: an
   * unpriced pairing and a pairing priced at a figure nobody can vouch
   * for are different problems with different fixes.
   */
  review?: boolean;
}

export function filterRateRows(rows: RateRow[], f: RateFilters): RateRow[] {
  const q = (f.q ?? "").trim().toLowerCase();
  return rows.filter((row) => {
    if (f.currency && row.currency !== f.currency) return false;
    if (f.network && row.asset.network_id !== f.network) return false;
    if (f.missing && row.active) return false;
    if (f.review && !needsUnitReview(row.active)) return false;
    if (!q) return true;
    return [
      row.asset.symbol,
      row.asset.asset_id,
      row.asset.network_id,
      row.networkName,
      row.currency,
    ]
      .join(" ")
      .toLowerCase()
      .includes(q);
  });
}

/** The row a deep link names, if it is still in the (filtered) table. */
export function rowKey(assetID: string, currency: string) {
  return `${assetID}|${currency}`;
}
